<template>
  <div class="checklist-table-header" data-locator="table-header">
    <div class="checklist-table-first-column checklist-table-header-cell px-1">
      <v-checkbox
        v-show="isCurrentUserPm"
        v-model="allTableItemsAreSelected"
        readonly
        hide-details
        data-locator="select-all-checkbox"
        :ripple="false"
        color="darkblue-500"
        @click.stop="toggleSelectAll"
      />
    </div>

    <div
      class="checklist-table-column-title checklist-table-header-cell"
      :class="{ 'with-border': isTableHorizontallyScrolled }"
    >
      <div class="mr-2">
        {{ t('task') }}
      </div>

      <AppTooltip>
        <template #activator="{ props }">
          <app-button
            type="icon"
            class="expand-all-button"
            data-locator="expand-all-button"
            v-bind="props"
            @click="expandAllSections"
          >
            <app-icon-ds
              icon="angles-up-down"
              size="xxs"
              weight="far"
            />
          </app-button>
        </template>
        {{ t("expandAllTooltip") }}
      </AppTooltip>
      <AppTooltip>
        <template #activator="{ props }">
          <app-button
            type="icon"
            class="collapse-all-button"
            data-locator="collapse-all-button"
            v-bind="props"
            @click="collapseAllSections"
          >
            <app-icon-ds
              icon="regular-angles-down-up"
              size="xxs"
              weight="fak"
            />
          </app-button>
        </template>
        {{ t("collapseAllTooltip") }}
      </AppTooltip>
    </div>

    <div
      v-for="({ label, key }) in computedHeaders"
      :key="key"
      class="checklist-table-header-cell"
    >
      <TruncatedText>{{ label }}</TruncatedText>
    </div>

    <div class="checklist-table-column-action checklist-table-header-cell" />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import TruncatedText from '@/common/TruncatedText.vue'
import { useTranslation } from '@/common/utils/translation'
import {
  ADD_ALL_ITEMS_TO_SELECTION,
  CLEAR_ITEMS_SELECTION,
  COLLAPSE_ALL_SECTIONS,
  EXPAND_ALL_SECTIONS,
} from '@/store/modules/checklist/action_types'

export default defineComponent({
  name: 'ChecklistTableHeader',
  components: { AppTooltip, TruncatedText },
  setup () {
    const isTableHorizontallyScrolled = inject<boolean>('isTableHorizontallyScrolled')
    const { t } = useTranslation({ keyPrefix: 'project.checklist.ChecklistTableHeader' })

    return {
      isTableHorizontallyScrolled,
      t,
    }
  },
  computed: {
    ...mapState('checklist', ['checklistTableHeader']),
    ...mapGetters('checklist', [
      'allSelectedChecklistItems',
      'isChecklistFiltered',
      'selectableChecklistItems',
      'selectedTasks',
    ]),
    ...mapGetters('room', ['isCurrentUserPm']),
    allTableItemsAreSelected: {
      get (): boolean {
        return this.selectableChecklistItems.length > 0 && this.displayedSelection.length === this.selectableChecklistItems.length
      },
      set (value: boolean) {
        if (value) {
          this.ADD_ALL_ITEMS_TO_SELECTION()
        } else {
          this.CLEAR_ITEMS_SELECTION()
        }
      },
    },
    computedHeaders() {
      return this.checklistTableHeader.map((header: string) => {
        if (header === 'newColumn') {
          return { key: header, label: 'newColumn' };
        }
        return { key: header, label: this.t(header) }
      })
    },
    displayedSelection () {
      return this.isChecklistFiltered ? this.selectedTasks : this.allSelectedChecklistItems
    },
  },
  methods: {
    ...mapActions('checklist', [
      ADD_ALL_ITEMS_TO_SELECTION,
      CLEAR_ITEMS_SELECTION,
      COLLAPSE_ALL_SECTIONS,
      EXPAND_ALL_SECTIONS,
    ]),
    expandAllSections () {
      this.EXPAND_ALL_SECTIONS()
    },
    collapseAllSections () {
      this.COLLAPSE_ALL_SECTIONS()
    },
    toggleSelectAll () {
      this.allTableItemsAreSelected = !this.allTableItemsAreSelected
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/project/checklist/table/checklist-table.scss";

.checklist-table-column-title {
  display: flex;
  align-items: center;
}

.checklist-table-header {
  height: 40px;
  display: grid;
  grid-template-columns: subgrid;
  grid-column: row-start / row-end;
  border-radius: 12px;
  background-color: rgb(var(--v-theme-grey-25));
  position: sticky;
  top: 0;
  z-index: 2;
}

.checklist-table-header-cell {
  display: flex;
  align-items: center;
  padding-inline: 12px;
  color: rgb(var(--v-theme-grey-700));
  font-weight: 400;
  background-color: rgb(var(--v-theme-grey-25));
  
  &:hover {
    cursor: default;
  }
}

.collapse-all-button,
.expand-all-button {
  &.app-button.app-button--icon {
    min-width: 28px;
    min-height: 28px;
    height: 28px;
  }
}
</style>
